import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { timestampSchema, uuidSchema } from "../common/primitives.js";
import { taxRateCodeSchema } from "./tax.js";
export const PRODUCT_TYPES = [
    "FUEL",
    "LPG",
    "MARKET_ITEM",
    "LUBRICANT",
    "WASH_SERVICE",
    "EV_ENERGY",
    "CAFE_ITEM",
    "SERVICE",
    "OTHER",
    // Prompt 18: sarf malzemesi (yıkama kimyasalı) — stoklu, SATILMAZ (kasa kataloğu ve ajan ürün ana verisi dışı).
    "CONSUMABLE",
];
export const productTypeSchema = z.enum(PRODUCT_TYPES);
export const PRODUCT_UNITS = ["LITRE", "KG", "PIECE", "KWH", "MINUTE"];
export const productUnitSchema = z.enum(PRODUCT_UNITS);
/** Net içerik birimi (etiket birim fiyatı ₺/kg, ₺/L — mevzuat sorusu #61). */
export const NET_CONTENT_UNITS = ["G", "KG", "ML", "L", "PIECE"];
export const netContentUnitSchema = z.enum(NET_CONTENT_UNITS);
export const NET_CONTENT_UNIT_LABELS = {
    G: "g",
    KG: "kg",
    ML: "ml",
    L: "L",
    PIECE: "adet",
};
export const FUEL_GRADES = [
    "GASOLINE_95",
    "GASOLINE_97",
    "DIESEL",
    "DIESEL_PREMIUM",
    "LPG_AUTOGAS",
    "KEROSENE",
    "ADBLUE",
    "OTHER",
];
export const fuelGradeSchema = z.enum(FUEL_GRADES);
/** Ürün türü → olağan iş birimi ve birim (form varsayılanları; kullanıcı değiştirebilir). */
export const PRODUCT_TYPE_DEFAULTS = {
    FUEL: { businessUnit: "FUEL", unit: "LITRE" },
    LPG: { businessUnit: "LPG", unit: "LITRE" },
    MARKET_ITEM: { businessUnit: "MARKET", unit: "PIECE" },
    LUBRICANT: { businessUnit: "MARKET", unit: "PIECE" },
    WASH_SERVICE: { businessUnit: "WASH", unit: "PIECE" },
    EV_ENERGY: { businessUnit: "EV", unit: "KWH" },
    CAFE_ITEM: { businessUnit: "CAFE", unit: "PIECE" },
    SERVICE: { businessUnit: "MARKET", unit: "PIECE" },
    OTHER: { businessUnit: "MARKET", unit: "PIECE" },
    CONSUMABLE: { businessUnit: "WASH", unit: "LITRE" },
};
const densitySchema = z
    .string()
    .regex(/^\d(\.\d{1,5})?$/, "Yoğunluk kg/L cinsinden, en fazla 5 ondalık (ör. 0.745)")
    .refine((value) => Number(value) > 0, "Yoğunluk sıfırdan büyük olmalıdır");
const expansionSchema = z
    .string()
    .regex(/^0(\.\d{1,7})?$/, "Genleşme katsayısı 1/°C cinsinden (ör. 0.00125)");
// ── Kategoriler ──────────────────────────────────────────────────────────────
export const productCategorySchema = z.object({
    id: uuidSchema,
    parentId: uuidSchema.nullable(),
    name: z.string(),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
    productCount: z.number().int(),
    /** Market (Prompt 16): hedef brüt marj (%); boşsa şirketin market ayarındaki varsayılan kullanılır. */
    targetMarginPercent: z
        .string()
        .nullable()
        .describe("Hedef brüt marj (%, 2 ondalık); boşsa şirket varsayılanı"),
});
export const productCategoryListSchema = z.object({
    items: z.array(productCategorySchema).describe("Düz liste; ağaç parentId ile kurulur"),
});
const marginPercentSchema = z
    .string()
    .regex(/^\d{1,2}(\.\d{1,2})?$/, "Marj 0 ile 99,99 arasında, en fazla 2 ondalıklı yüzde olmalıdır")
    .describe('Yüzde (dizge). Örn. "25" = %25');
export const createProductCategorySchema = z.object({
    name: z.string().trim().min(1, "Kategori adı zorunludur").max(120),
    parentId: uuidSchema.nullable().default(null),
    sortOrder: z.number().int().min(0).max(9999).default(0),
    targetMarginPercent: marginPercentSchema
        .nullable()
        .default(null)
        .describe("Hedef brüt marj (%); satış fiyatı önerisi bunu kullanır (ADR 0016 §16)"),
});
export const updateProductCategorySchema = patchOf(createProductCategorySchema).extend({
    isActive: z.boolean().optional(),
});
// ── Ürünler ──────────────────────────────────────────────────────────────────
export const productBarcodeSchema = z.object({ barcode: z.string(), isPrimary: z.boolean() });
export const productSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    productType: productTypeSchema,
    name: z.string(),
    shortName: z.string().nullable(),
    unit: productUnitSchema,
    vatRateCode: z.string(),
    vatRate: z.string().nullable().describe("Bugün geçerli KDV oranı (%); kod çözülemiyorsa null"),
    accountingGroup: z.string().describe("Muhasebe eşleştirme anahtarı"),
    businessUnit: businessUnitTypeSchema,
    categoryId: uuidSchema.nullable(),
    categoryName: z.string().nullable(),
    specialConsumptionTaxIncluded: z.boolean().describe("ÖTV fiyatın içinde ve maliyete dâhil (bilgi alanı)"),
    fuelGrade: fuelGradeSchema.nullable(),
    defaultDensity: z.string().nullable().describe("kg/L (15 °C)"),
    thermalExpansionCoefficient: z.string().nullable().describe("1/°C; 15 °C hacim düzeltmesi için"),
    color: z.string().nullable(),
    /** Yerel kasa (Prompt 15): yaş sınırlı ürün — satışta personel onayı ister. */
    isAgeRestricted: z.boolean(),
    /** Yerel kasa hızlı tuş yuvası (1–48); tekil DEĞİLDİR. */
    posQuickKeySlot: z.number().int().nullable(),
    // ── Market (Prompt 16) ──────────────────────────────────────────────────
    brand: z.string().nullable(),
    trackExpiry: z.boolean().describe("SKT takibi: mal kabulde parti + son kullanma tarihi, çıkışta FEFO"),
    packSize: z.string().nullable().describe("Koli / paket içi adet (sipariş bu katlara yuvarlanır)"),
    netContent: z.string().nullable().describe("Net içerik miktarı (etiket birim fiyatı)"),
    netContentUnit: netContentUnitSchema.nullable(),
    isActive: z.boolean(),
    barcodes: z.array(productBarcodeSchema),
    version: z.number().int(),
    createdAt: timestampSchema,
});
export const productPageSchema = pageOf(productSchema);
const positiveQuantity = z
    .string()
    .regex(/^\d{1,15}(\.\d{1,3})?$/, "En fazla 3 ondalıklı pozitif sayı olmalıdır")
    .refine((value) => Number(value) > 0, "Sıfırdan büyük olmalıdır");
const productFields = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9._-]{0,29}$/, "Kod 1–30 karakter; büyük harf, rakam, nokta, - ve _ içerebilir"),
    productType: productTypeSchema,
    name: z.string().trim().min(1, "Ürün adı zorunludur").max(200),
    shortName: z.string().trim().max(40).nullable().default(null),
    unit: productUnitSchema,
    vatRateCode: taxRateCodeSchema,
    accountingGroup: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Muhasebe grubu büyük harf, rakam ve _ içerebilir (ör. FUEL_GASOLINE)"),
    businessUnit: businessUnitTypeSchema,
    categoryId: uuidSchema.nullable().default(null),
    specialConsumptionTaxIncluded: z.boolean().default(false),
    fuelGrade: fuelGradeSchema.nullable().default(null),
    defaultDensity: densitySchema.nullable().default(null),
    thermalExpansionCoefficient: expansionSchema.nullable().default(null),
    color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, "Renk #RRGGBB biçiminde olmalıdır")
        .nullable()
        .default(null),
    // ── Yerel kasa (Prompt 15) ────────────────────────────────────────────────
    isAgeRestricted: z.boolean().default(false).describe("Kasa satışında personel onayı ister"),
    posQuickKeySlot: z
        .number()
        .int()
        .min(1)
        .max(48)
        .nullable()
        .default(null)
        .describe("Kasa hızlı tuş yuvası (1–48); NULL = hızlı tuşta yok"),
    // ── Market (Prompt 16) ────────────────────────────────────────────────────
    brand: z.string().trim().min(1).max(80).nullable().default(null).describe("Marka"),
    trackExpiry: z
        .boolean()
        .default(false)
        .describe("SKT takibi: mal kabulde parti + son kullanma tarihi zorunlu, çıkışlar FEFO ile partiden düşer"),
    packSize: positiveQuantity.nullable().default(null).describe("Koli / paket içi adet"),
    netContent: positiveQuantity
        .nullable()
        .default(null)
        .describe("Net içerik miktarı (ör. 500 g → 500); `netContentUnit` ile birlikte verilir"),
    netContentUnit: netContentUnitSchema.nullable().default(null),
});
const fuelRules = (value, context) => {
    if (value.fuelGrade && value.productType && !["FUEL", "LPG"].includes(value.productType))
        context.addIssue({
            code: "custom",
            path: ["fuelGrade"],
            message: "Yakıt sınıfı yalnızca akaryakıt ve LPG ürünlerinde kullanılır",
        });
    if (value.productType === "LPG" && !value.defaultDensity)
        context.addIssue({
            code: "custom",
            path: ["defaultDensity"],
            message: "LPG'de kg ↔ litre dönüşümü için yoğunluk zorunludur",
        });
};
/** Net içerik ve birimi BİRLİKTE verilir ya da ikisi de boş kalır (veritabanı kısıtı `products_net_content_chk`). */
const netContentRule = (value, context) => {
    if (value.netContent === undefined && value.netContentUnit === undefined)
        return;
    if ((value.netContent ?? null) === null && (value.netContentUnit ?? null) !== null)
        context.addIssue({
            code: "custom",
            path: ["netContent"],
            message: "Net içerik birimi seçildiyse miktarı da girin",
        });
    if ((value.netContent ?? null) !== null && (value.netContentUnit ?? null) === null)
        context.addIssue({
            code: "custom",
            path: ["netContentUnit"],
            message: "Net içerik miktarı girildiyse birimini de seçin",
        });
};
export const createProductSchema = productFields.superRefine((value, context) => {
    fuelRules(value, context);
    netContentRule(value, context);
});
export const updateProductSchema = patchOf(productFields.omit({ code: true })).extend({
    isActive: z.boolean().optional(),
    version: z.number().int().positive(),
});
export const listProductsQuerySchema = pageQuerySchema.extend({
    search: z.string().trim().max(100).optional().describe("Ad, kod ya da barkod"),
    productType: productTypeSchema.optional(),
    businessUnit: businessUnitTypeSchema.optional(),
    categoryId: uuidSchema.optional(),
    isActive: z.enum(["true", "false"]).optional(),
});
export const setProductBarcodesSchema = z.object({
    barcodes: z
        .array(z.object({
        barcode: z
            .string()
            .trim()
            .regex(/^[0-9A-Za-z._-]{4,48}$/, "Barkod 4–48 karakter; harf, rakam, nokta, - ve _ içerebilir"),
        isPrimary: z.boolean().default(false),
    }))
        .max(50)
        .refine((items) => new Set(items.map((item) => item.barcode)).size === items.length, "Aynı barkod iki kez girilemez")
        .refine((items) => items.filter((item) => item.isPrimary).length <= 1, "En fazla bir ana barkod seçilebilir"),
});
export const productLookupQuerySchema = z.object({ barcode: z.string().trim().min(4).max(48) });
//# sourceMappingURL=products.js.map